/**
 * CASL ability context instance + core ability types.
 *
 * IMPORTANT — why createContext lives here and not in AbilityContext.tsx:
 *
 * Vite / React Fast Refresh re-executes a module (re-running `createContext`, which mints a
 * brand-new context object) whenever that module *or one of its runtime imports* is edited.
 * AbilityContext.tsx imports Auth + ability and exports hooks + components, so it is not a clean
 * Fast Refresh boundary: saving Auth.tsx / ability.ts / AbilityContext.tsx during development
 * forces it to re-run. If the context object were created there, each such save would produce a
 * fresh context — the <AbilityContext.Provider> higher in the tree would keep the old object
 * while consumers (e.g. the sidebar's useAbility()) read the new one, and those consumers would
 * fall back to THIS default empty ability. The result: permission-gated UI silently disappears
 * after an HMR update until a full page reload.
 *
 * Keeping this module's runtime imports minimal (only @casl/ability) means it is not re-executed
 * during normal editing, so the single context instance stays stable across HMR.
 */
import { createContext } from 'react';
import { createMongoAbility, type MongoAbility } from '@casl/ability';
import { type AdminAction } from '@/app/models/AdminAction';
import { type Resource } from './resources';

// ============================================================================
// TYPES
// ============================================================================

export type Action = `${AdminAction}` | 'manage';
export type Subject = Resource | 'all';

export type AppAbility = MongoAbility<[Action, Subject]>;

// ============================================================================
// EMPTY ABILITY (default for non-authenticated users / default context value)
// ============================================================================

export function createEmptyAbility(): AppAbility {
  return createMongoAbility<[Action, Subject]>();
}

// ============================================================================
// CONTEXT INSTANCE (single, stable across HMR)
// ============================================================================

export const AbilityContext = createContext<AppAbility>(createEmptyAbility());
